import React, { PropsWithChildren, RefObject } from 'react';
import {
  StyleSheet,
  Platform,
  ScrollView,
  View,
  useWindowDimensions,
  Modal,
  TouchableWithoutFeedback,
} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { withHapticFeedback } from 'lib/withHapticFeedback';
import { theme } from 'lib/theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import reactStringReplace from 'react-string-replace';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { useDispatch } from 'react-redux';
import { createAction } from 'typesafe-actions';
import {
  Button,
  BUTTON_COMPACT_HEIGHT,
  BUTTON_HEIGHT,
  ButtonProps,
} from './Button';
import { Icon, ICONS } from './Icon';
import { IconButton, IconButtonProps } from './IconButton';
import { SeparatorHorizontal } from './SeparatorHorizontal';
import { ViewWithSeparators } from './ViewWithSeparators';
import { Txt, TxtProps } from './Txt';
import { Pressable } from './Pressable';

type MenuRef = {
  close: () => void;
};

type DefinedMenuOption = {
  key?: string;
  text: string;
  subText?: string;
  horizontalText?: boolean;
  onPress?: () => void;
  icon?: ICONS;
  iconColor?: string;
  leftIcon?: ICONS;
  leftIconColor?: string;
  textColor?: string;
  destructive?: boolean;
  textNumberOfLines?: number;
  textSize?: TxtProps['size'];
  keepMenuOpenOnPress?: boolean;
};

export type MenuOption = DefinedMenuOption | '-' | string | React.ReactElement;

type Props = {
  visible?: boolean;
  options: MenuOption[];
  onDismiss?: () => void;
  width?: number;
  marginTop?: number;

  x?: number | undefined;
  y?: number | undefined;
  parentHeight?: number | undefined;
  parentWidth?: number | undefined;
  parentX?: number | undefined;
  parentY?: number | undefined;
  height?: number;
  offsetX?: number;
  disabled?: boolean;
  ref?: RefObject<MenuRef>;
};

type PropsWithAnchor = {
  anchor?: React.ReactElement;
  buttonConfig?: never;
  iconButtonConfig?: never;
};

type PropsWithIconButton = {
  anchor?: never;
  buttonConfig?: never;
  iconButtonConfig?: Omit<IconButtonProps, 'onPress' | 'onPressIn'>;
};

type PropsWithButton = {
  anchor?: never;
  buttonConfig?: Omit<ButtonProps, 'onPress' | 'onPressIn'>;
  iconButtonConfig?: never;
};

const typeGuard = <T extends Record<string, unknown>>(
  toBeDetermined: unknown,
): toBeDetermined is T => !(toBeDetermined as T).type;

export type MenuProps = Props &
  (PropsWithIconButton | PropsWithButton | PropsWithAnchor);

const eventCloseMenus = createAction('doCloseMenus')();
const MENU_ANIMATION_DURATION = 190;

export const Menu = ({
  anchor,
  buttonConfig,
  iconButtonConfig,
  onDismiss,
  options,
  visible,
  width = 250,
  marginTop = 0,
  x,
  y,
  parentHeight,
  parentX,
  parentY,
  height,
  offsetX,
  disabled,
  ref,
}: PropsWithChildren<MenuProps>) => {
  const dispatch = useDispatch();
  const [internalVisible, setInternalVisible] = React.useState<boolean>(false);
  const [modalVisible, setModalVisible] = React.useState<boolean>(false);
  const anchorRef = React.useRef<View>(null);
  const animationProgress = useSharedValue(0);
  const allowInteraction = React.useRef<boolean>(false);
  const allowInteractionTimeout =
    React.useRef<ReturnType<typeof setTimeout>>(null);
  const modalVisibleTimeout = React.useRef<ReturnType<typeof setTimeout>>(null);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const menuWidth = Math.min(width, windowWidth - theme.padding * 2);
  const [menuDims, setMenuDims] = React.useState({ width: 0, height: 0 });
  const [anchorDims, setAnchorDims] = React.useState<{
    parentWidth?: number;
    parentHeight?: number;
    parentX?: number;
    parentY?: number;
  }>({});
  const { top: topSafeArea } = useSafeAreaInsets();
  const androidModalTopOffset = Platform.OS === 'android' ? topSafeArea : 0;
  // const centerX = centered ? windowWidth / 2 - width / 2 : undefined;
  let customX: number | undefined;
  let customY: number | undefined;

  const measuredParentX = parentX ?? anchorDims.parentX;
  const measuredParentY = parentY ?? anchorDims.parentY;
  const measuredParentHeight = parentHeight ?? anchorDims.parentHeight;
  const isUsingMeasuredAnchor =
    parentX === undefined &&
    parentY === undefined &&
    anchorDims.parentX !== undefined &&
    anchorDims.parentY !== undefined;
  const useCustomPosition =
    x !== undefined ||
    y !== undefined ||
    measuredParentX !== undefined ||
    measuredParentY !== undefined;

  const allowedMaxHeight = Math.min(
    height || 9999,
    windowHeight - (topSafeArea + 80),
  );
  let allowedPositionedMaxHeight = allowedMaxHeight;
  let measuredMenuHeight =
    menuDims.height > 0 ? Math.min(menuDims.height, allowedMaxHeight) : 0;

  // Custom X
  if (x !== undefined) {
    customX = x;
  } else if (useCustomPosition && measuredParentX !== undefined) {
    customX = measuredParentX;
  }
  if (customX !== undefined) {
    const maxX = Math.max(
      theme.padding,
      windowWidth - menuWidth - theme.padding,
    );
    customX = Math.min(Math.max(customX, theme.padding), maxX);
  }

  const menuMarginTop =
    marginTop !== 0
      ? marginTop
      : (buttonConfig?.compact ? BUTTON_COMPACT_HEIGHT : BUTTON_HEIGHT) + 6;
  const effectiveMenuMarginTop =
    isUsingMeasuredAnchor && useCustomPosition ? 6 : menuMarginTop;

  // Custom Y
  if (y !== undefined) {
    customY = y + effectiveMenuMarginTop;
  } else if (
    useCustomPosition &&
    measuredParentY !== undefined &&
    measuredParentHeight !== undefined
  ) {
    customY =
      measuredParentY +
      measuredParentHeight +
      effectiveMenuMarginTop +
      androidModalTopOffset;
  }
  if (
    customY !== undefined &&
    measuredMenuHeight > 0 &&
    customY + measuredMenuHeight + 20 > windowHeight
  ) {
    const minMenuTop = topSafeArea + 40;
    const menuTopAbove =
      y !== undefined
        ? y - measuredMenuHeight - effectiveMenuMarginTop
        : measuredParentY !== undefined
          ? measuredParentY -
            measuredMenuHeight -
            effectiveMenuMarginTop +
            androidModalTopOffset
          : undefined;

    if (menuTopAbove !== undefined && menuTopAbove >= minMenuTop) {
      customY = menuTopAbove;
    } else if (
      measuredParentY !== undefined &&
      measuredParentHeight !== undefined
    ) {
      customY =
        measuredParentY +
        measuredParentHeight +
        effectiveMenuMarginTop +
        androidModalTopOffset;
    }
  }
  if (customY !== undefined) {
    allowedPositionedMaxHeight = Math.min(
      allowedPositionedMaxHeight,
      Math.max(44, windowHeight - customY - 20),
    );
    measuredMenuHeight =
      menuDims.height > 0
        ? Math.min(menuDims.height, allowedPositionedMaxHeight)
        : 0;
  }

  const handleOpen = React.useCallback(() => {
    if (anchorRef.current?.measureInWindow) {
      anchorRef.current.measureInWindow(
        (currentX, currentY, measuredWidth, measuredHeight) => {
          setAnchorDims({
            parentHeight: measuredHeight,
            parentWidth: measuredWidth,
            parentX: currentX,
            parentY: currentY,
          });
          setInternalVisible(true);
        },
      );
      return;
    }
    setInternalVisible(true);
  }, []);

  const internalAnchorContent =
    anchor ||
    (buttonConfig ? (
      <Button
        {...buttonConfig}
        disabled={disabled || buttonConfig.disabled}
        onPress={disabled ? undefined : withHapticFeedback(handleOpen)}
      />
    ) : iconButtonConfig ? (
      <IconButton
        {...iconButtonConfig}
        onPress={disabled ? undefined : withHapticFeedback(handleOpen)}
      />
    ) : null);

  const internalAnchor: React.ReactElement | null = internalAnchorContent ? (
    <View ref={anchorRef} collapsable={false}>
      {internalAnchorContent}
    </View>
  ) : null;

  const handleDismiss = React.useCallback(() => {
    if (allowInteraction.current) {
      allowInteraction.current = false;
      setInternalVisible(false);
      onDismiss?.();
    }
  }, [onDismiss]);

  React.useImperativeHandle(
    ref,
    () => ({
      close: handleDismiss,
    }),
    [handleDismiss],
  );

  const isVisible = !!visible || internalVisible;

  React.useEffect(() => {
    if (modalVisibleTimeout.current) {
      clearTimeout(modalVisibleTimeout.current);
      modalVisibleTimeout.current = null;
    }

    if (isVisible) {
      setModalVisible(true);
      animationProgress.value = 0;
      animationProgress.value = withTiming(1, {
        duration: MENU_ANIMATION_DURATION,
        easing: Easing.bezier(0.4, 0, 0.2, 1),
      });
      return;
    }

    animationProgress.value = withTiming(0, {
      duration: MENU_ANIMATION_DURATION,
      easing: Easing.bezier(0.4, 0, 0.2, 1),
    });
    modalVisibleTimeout.current = setTimeout(() => {
      setModalVisible(false);
      modalVisibleTimeout.current = null;
    }, MENU_ANIMATION_DURATION);

    return () => {
      if (modalVisibleTimeout.current) {
        clearTimeout(modalVisibleTimeout.current);
        modalVisibleTimeout.current = null;
      }
    };
  }, [animationProgress, isVisible]);

  const animatedMenuStyle = useAnimatedStyle(() => ({
    opacity: animationProgress.value,
    transform: [
      {
        scale: 0.94 + animationProgress.value * 0.06,
      },
    ],
  }));

  const handleContextPress = React.useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      handleDismiss();
    },
    [handleDismiss],
  );

  React.useEffect(() => {
    if (Platform.OS === 'web' && isVisible) {
      document.addEventListener('contextmenu', handleContextPress);
    }
    return () => {
      if (Platform.OS === 'web' && isVisible) {
        document.removeEventListener('contextmenu', handleContextPress);
      }
    };
  }, [handleContextPress, isVisible]);

  React.useEffect(() => {
    const events: number[] = [];
    if (allowInteractionTimeout.current) {
      clearTimeout(allowInteractionTimeout.current);
      allowInteractionTimeout.current = null;
    }
    if (isVisible) {
      allowInteractionTimeout.current = setTimeout(() => {
        allowInteraction.current = true;
        allowInteractionTimeout.current = null;
      }, 300);
      events.push(
        doAddEvent(eventCloseMenus, () => {
          handleDismiss();
        }),
      );
    }
    return () => {
      if (allowInteractionTimeout.current) {
        clearTimeout(allowInteractionTimeout.current);
        allowInteractionTimeout.current = null;
      }
      if (events.length) {
        doRemoveEvents(events);
      }
    };
  }, [handleDismiss, isVisible]);

  let hasOptionAbove = false;
  const bold = /\*(.*?)\*/gm;
  const menuLeft = customX !== undefined ? customX + (offsetX || 0) : undefined;
  const menuTop = customY;

  return (
    <>
      {internalAnchor}
      <Modal
        visible={modalVisible}
        onRequestClose={handleDismiss}
        supportedOrientations={[
          'portrait',
          'portrait-upside-down',
          'landscape',
          'landscape-left',
          'landscape-right',
        ]}
        transparent
        statusBarTranslucent
        navigationBarTranslucent
      >
        <TouchableWithoutFeedback onPress={handleDismiss} accessible={false}>
          <View style={StyleSheet.absoluteFill} />
        </TouchableWithoutFeedback>
        <Animated.View
          style={[
            styles.menu,
            {
              left: menuLeft ?? theme.padding,
              top: menuTop ?? topSafeArea + 40,
            },
            animatedMenuStyle,
            disabled ? styles.menuDisabled : undefined,
          ]}
          onLayout={(event) => {
            const { width: componentWidth, height: componentHeight } =
              event.nativeEvent.layout;
            setMenuDims((current) =>
              current.width === componentWidth &&
              current.height === componentHeight
                ? current
                : { width: componentWidth, height: componentHeight },
            );
          }}
        >
          <ScrollView
            style={{
              maxHeight: allowedPositionedMaxHeight,
              pointerEvents: 'auto',
            }}
          >
            <ViewWithSeparators>
              {options.map((option, index, array) => {
                if (option === '-') {
                  const nextOption = array[index + 1];
                  if (hasOptionAbove && nextOption && nextOption !== '-') {
                    hasOptionAbove = false;
                    return (
                      <SeparatorHorizontal
                        key={Math.random()}
                        style={{
                          backgroundColor:
                            Platform.OS === 'ios' ? '#999' : '#ddd',
                          height:
                            Platform.OS === 'ios'
                              ? StyleSheet.hairlineWidth
                              : 1,
                          marginTop: -(Platform.OS === 'ios'
                            ? StyleSheet.hairlineWidth
                            : 1),
                          marginBottom: -(Platform.OS === 'ios'
                            ? StyleSheet.hairlineWidth
                            : 1),
                        }}
                      />
                    );
                  }
                  return null;
                }
                hasOptionAbove = true;
                if (typeof option === 'string') {
                  return (
                    <View
                      key={option}
                      style={{
                        padding: theme.padding,
                        width: menuWidth,
                        backgroundColor: '#f1f1f1',
                        borderTopWidth: 1,
                        borderBottomWidth: 1,
                        borderColor: '#eaeaea',
                      }}
                    >
                      <Txt size="xxxsmall" weight="bold">
                        {option}
                      </Txt>
                    </View>
                  );
                }

                if (React.isValidElement(option)) {
                  return option;
                }

                if (!typeGuard<DefinedMenuOption>(option)) {
                  return null;
                }

                return (
                  <Pressable
                    key={option.key || option.text}
                    style={{
                      padding: theme.padding,
                      width: menuWidth,
                      minHeight: 44,
                    }}
                    disabled={disabled || !option.onPress}
                    hoveredStyle={{ backgroundColor: '#f1f1f1' }}
                    pressedStyle={{ backgroundColor: '#dddddd' }}
                    onPress={
                      option.onPress
                        ? () => {
                            if (allowInteraction.current) {
                              if (!option.keepMenuOpenOnPress) {
                                dispatch(eventCloseMenus());
                              }
                              setTimeout(() => {
                                option.onPress?.();
                              }, MENU_ANIMATION_DURATION);
                            }
                          }
                        : undefined
                    }
                  >
                    <ViewWithSeparators
                      horizontal
                      separatorMargin={theme.padding / 2}
                      separatorColor="transparent"
                      style={{ flex: 1, alignItems: 'center' }}
                    >
                      {option.leftIcon && (
                        <Icon
                          source={option.leftIcon}
                          color={option.leftIconColor}
                          size={12}
                          style={
                            !option.onPress ? { opacity: 0.42 } : undefined
                          }
                        />
                      )}
                      <View
                        style={[
                          { flex: 1 },
                          option.horizontalText
                            ? { flexDirection: 'row', alignItems: 'center' }
                            : undefined,
                        ]}
                      >
                        <Txt
                          size={option.textSize}
                          style={[
                            option.destructive || option.textColor
                              ? {
                                  color: option.textColor || theme.colors.red,
                                }
                              : undefined,
                            option.onPress ? undefined : { opacity: 0.42 },
                            option.horizontalText ? { flex: 1 } : { flex: 1 },
                          ]}
                          numberOfLines={
                            option.textNumberOfLines !== undefined
                              ? option.textNumberOfLines
                              : 1
                          }
                        >
                          {reactStringReplace(option.text, bold, (match, i) => (
                            <Txt key={i} weight="bold" size={option.textSize}>
                              {match}
                            </Txt>
                          ))}
                        </Txt>
                        {!!option.subText && (
                          <Txt
                            size={option.horizontalText ? undefined : 'xsmall'}
                            style={[
                              {
                                color: theme.colors.fieldValueText,
                              },
                              option.horizontalText
                                ? { textAlign: 'right' }
                                : { marginTop: 3, flex: 1 },
                            ]}
                          >
                            {reactStringReplace(
                              option.subText,
                              bold,
                              (match, i) => (
                                <Txt key={i} weight="bold">
                                  {match}
                                </Txt>
                              ),
                            )}
                          </Txt>
                        )}
                      </View>
                      {option.icon && (
                        <Icon
                          source={option.icon}
                          color={
                            option.iconColor ||
                            (option.destructive ? theme.colors.red : undefined)
                          }
                          style={
                            !option.onPress ? { opacity: 0.42 } : undefined
                          }
                        />
                      )}
                    </ViewWithSeparators>
                  </Pressable>
                );
              })}
            </ViewWithSeparators>
          </ScrollView>
        </Animated.View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  menu: {
    position: 'absolute',
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: [
      {
        color: theme.colors.black30,
        offsetX: 0,
        offsetY: 0,
        blurRadius: 50,
      },
    ],
    backgroundColor: '#fafafa',
  },
  menuDisabled: { opacity: 0.42, pointerEvents: 'none' },
});
